"use client";

/**
 * Transient in-app notices (tdd-0079 §6).
 *
 * The toast is a *view* of the activity store, not a store of its own —
 * dismissing one removes the toast and leaves the item in the feed. That is
 * what makes a toast safe to swat.
 *
 * ## Accessibility is not optional here
 *
 * A toast is an interruption by definition, and interruptions are where
 * assistive technology usually gets forgotten. Radix is doing the load-bearing
 * work — live region, focus management, an escape hatch to the toast region via
 * F6 — which is most of why this is built on the primitive rather than
 * hand-rolled.
 *
 * Two rules on top of what Radix gives us:
 *
 * - `assertive` only for `urgent`. An assertive live region interrupts a screen
 *   reader mid-sentence, and a save confirmation does not warrant that.
 * - A **sticky** toast must be dismissible by keyboard. A sticky element that
 *   can only be closed with a mouse is a permanent obstruction for anyone who
 *   does not use one.
 */

import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@workspace/ui/lib/utils";

const ToastProvider = ToastPrimitive.Provider;

export type ToastViewportProps = React.ComponentPropsWithoutRef<
  typeof ToastPrimitive.Viewport
>;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  ToastViewportProps
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    data-slot="toast-viewport"
    className={cn(
      "fixed z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4",
      // Bottom-right on desktop, top on mobile — where a thumb is least likely
      // to be, and where it does not sit over a mobile keyboard.
      "top-0 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[400px]",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

const toastVariants = cva(
  cn(
    "group pointer-events-auto relative flex w-full items-start gap-3",
    "overflow-hidden rounded-lg border p-4 pr-8 shadow-lg",
    "transition-all",
    "data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-full",
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-80",
    "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]",
    "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:animate-out",
  ),
  {
    variants: {
      // Mirrors the notification level, so an urgent notice reads as urgent
      // without the caller choosing a colour.
      level: {
        info: "border-border bg-background text-foreground",
        action: "border-primary/30 bg-background text-foreground",
        urgent: "border-destructive/50 bg-background text-foreground",
      },
    },
    defaultVariants: { level: "info" },
  },
);

export type ToastProps = React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root> &
  VariantProps<typeof toastVariants>;

const Toast = React.forwardRef<React.ElementRef<typeof ToastPrimitive.Root>, ToastProps>(
  ({ className, level, ...props }, ref) => (
    <ToastPrimitive.Root
      ref={ref}
      data-slot="toast"
      className={cn(toastVariants({ level }), className)}
      {...props}
    />
  ),
);
Toast.displayName = ToastPrimitive.Root.displayName;

export type ToastActionProps = React.ComponentPropsWithoutRef<
  typeof ToastPrimitive.Action
>;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Action>,
  ToastActionProps
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Action
    ref={ref}
    data-slot="toast-action"
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border px-3",
      "text-sm font-medium transition-colors hover:bg-secondary",
      "focus:outline-none focus:ring-2 focus:ring-ring",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitive.Action.displayName;

export type ToastCloseProps = React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  ToastCloseProps
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    data-slot="toast-close"
    // Always reachable, never hover-gated. A close control that appears on
    // hover is not there at all for a keyboard or touch user, and on a sticky
    // toast that makes it a permanent obstruction.
    className={cn(
      "absolute right-1 top-1 rounded-md p-1 text-muted-foreground",
      "transition-colors hover:text-foreground",
      "focus:outline-none focus:ring-2 focus:ring-ring",
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="size-4" aria-hidden="true" />
    <span className="sr-only">Dismiss</span>
  </ToastPrimitive.Close>
));
ToastClose.displayName = ToastPrimitive.Close.displayName;

export type ToastTitleProps = React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  ToastTitleProps
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    data-slot="toast-title"
    className={cn("text-sm font-medium leading-snug", className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

export type ToastDescriptionProps = React.ComponentPropsWithoutRef<
  typeof ToastPrimitive.Description
>;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  ToastDescriptionProps
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    data-slot="toast-description"
    className={cn("mt-0.5 text-sm text-muted-foreground", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

export {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  toastVariants,
};
